import { useEffect } from 'react';
import {
  LOCALES,
  HREFLANG_TAGS,
  OG_LOCALE_TAGS,
  SITE_LOCALE,
  SITE_ORIGIN,
  isMarketingPath,
  urlOnLocale,
} from '@/lib/site-config';

interface DocumentMeta {
  title: string;
  description: string;
}

function setMetaContent(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function clearAlternateHreflangs() {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
}

function appendAlternate(hreflang: string, href: string) {
  const link = document.createElement('link');
  link.setAttribute('rel', 'alternate');
  link.setAttribute('hreflang', hreflang);
  link.setAttribute('href', href);
  document.head.appendChild(link);
}

/**
 * Cross-domain hreflang: each marketing path exists on logpulse.io / .nl /
 * .de / .fr. We emit alternates for all 4 plus an x-default to .io.
 */
export function applyHreflang(pathname: string) {
  clearAlternateHreflangs();
  if (!isMarketingPath(pathname)) return;
  for (const loc of LOCALES) {
    appendAlternate(HREFLANG_TAGS[loc], urlOnLocale(pathname, loc));
  }
  appendAlternate('x-default', urlOnLocale(pathname, 'en'));
}

export function useDocumentMeta({ title, description }: DocumentMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') ?? '';

    document.title = title;
    metaDesc?.setAttribute('content', description);

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    const canonicalUrl = `${SITE_ORIGIN}${pathname === '/' ? '' : pathname}`;

    setCanonical(canonicalUrl);
    setMetaContent('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMetaContent('meta[property="og:title"]', 'property', 'og:title', title);
    setMetaContent('meta[property="og:description"]', 'property', 'og:description', description);
    setMetaContent('meta[property="og:locale"]', 'property', 'og:locale', OG_LOCALE_TAGS[SITE_LOCALE]);
    setMetaContent('meta[property="twitter:url"]', 'property', 'twitter:url', canonicalUrl);
    setMetaContent('meta[property="twitter:title"]', 'property', 'twitter:title', title);
    setMetaContent('meta[property="twitter:description"]', 'property', 'twitter:description', description);

    applyHreflang(pathname);

    return () => {
      document.title = prevTitle;
      metaDesc?.setAttribute('content', prevDesc);
    };
  }, [title, description]);
}
